import { useCallback, useEffect, useRef, useState } from 'react'
import useFetch from './useFetch'

export const EXPORT_JOB_STATUS = {
  IDLE: 'idle',
  QUEUED: 'queued',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  FAILED: 'failed',
  TRACKING_ERROR: 'tracking-error'
}

const ACTIVE_STATUSES = [EXPORT_JOB_STATUS.QUEUED, EXPORT_JOB_STATUS.PROCESSING]
const FAST_POLL_DURATION = 2 * 60 * 1000
const FAST_POLL_INTERVAL = 3000
const SLOW_POLL_INTERVAL = 10000
const MAX_CONSECUTIVE_FAILURES = 3

export const getExportPollDelay = (elapsed) =>
  elapsed < FAST_POLL_DURATION ? FAST_POLL_INTERVAL : SLOW_POLL_INTERVAL

const readStoredJob = (storageKey) => {
  try {
    const value = window.sessionStorage.getItem(storageKey)
    if (!value) return null
    const parsed = JSON.parse(value)
    return parsed?.jobId && parsed?.startedAt ? { ...parsed, storageKey } : null
  } catch {
    window.sessionStorage.removeItem(storageKey)
    return null
  }
}

const useExportJob = ({
  statusApi,
  storageKey,
  statusPayload = {},
  onCompleted,
  onFailed,
  onTrackingError
}) => {
  const { execute: getExportStatus } = useFetch(statusApi)
  const callbacksRef = useRef({ onCompleted, onFailed, onTrackingError })
  const statusPayloadRef = useRef(statusPayload)
  const [job, setJob] = useState(null)
  const [status, setStatus] = useState(EXPORT_JOB_STATUS.IDLE)
  const [isLongRunning, setIsLongRunning] = useState(false)

  useEffect(() => {
    callbacksRef.current = { onCompleted, onFailed, onTrackingError }
  }, [onCompleted, onFailed, onTrackingError])

  useEffect(() => {
    statusPayloadRef.current = statusPayload
  }, [statusPayload])

  useEffect(() => {
    const storedJob = readStoredJob(storageKey)
    setJob(storedJob)
    setStatus(storedJob ? EXPORT_JOB_STATUS.QUEUED : EXPORT_JOB_STATUS.IDLE)
    setIsLongRunning(false)
  }, [storageKey])

  const startTracking = useCallback(
    (jobId) => {
      if (jobId === undefined || jobId === null || jobId === '') return false

      const nextJob = {
        jobId: String(jobId),
        startedAt: Date.now(),
        storageKey
      }
      window.sessionStorage.setItem(storageKey, JSON.stringify(nextJob))
      setJob(nextJob)
      setStatus(EXPORT_JOB_STATUS.QUEUED)
      setIsLongRunning(false)
      return true
    },
    [storageKey]
  )

  useEffect(() => {
    if (!job?.jobId || job.storageKey !== storageKey) return undefined

    let isActive = true
    let timerId
    let consecutiveFailures = 0

    const removeStoredJob = () => {
      window.sessionStorage.removeItem(storageKey)
      setJob(null)
    }

    const scheduleNext = () => {
      const elapsed = Date.now() - job.startedAt
      setIsLongRunning(elapsed >= FAST_POLL_DURATION)
      timerId = window.setTimeout(poll, getExportPollDelay(elapsed))
    }

    const poll = () => {
      getExportStatus(
        {
          payload: {
            jobId: job.jobId,
            ...statusPayloadRef.current
          }
        },
        {
          onSuccess: ({ data }) => {
            if (!isActive) return
            consecutiveFailures = 0

            if (ACTIVE_STATUSES.includes(data?.status)) {
              setStatus(data.status)
              scheduleNext()
              return
            }

            if (data?.status === EXPORT_JOB_STATUS.COMPLETED) {
              removeStoredJob()
              setStatus(EXPORT_JOB_STATUS.COMPLETED)
              setIsLongRunning(false)
              callbacksRef.current.onCompleted?.()
              return
            }

            if (data?.status === EXPORT_JOB_STATUS.FAILED) {
              removeStoredJob()
              setStatus(EXPORT_JOB_STATUS.FAILED)
              setIsLongRunning(false)
              callbacksRef.current.onFailed?.()
              return
            }

            removeStoredJob()
            setStatus(EXPORT_JOB_STATUS.TRACKING_ERROR)
            callbacksRef.current.onTrackingError?.()
          },
          onFailed: (error) => {
            if (!isActive) return

            if (error?.response?.status === 404) {
              removeStoredJob()
              setStatus(EXPORT_JOB_STATUS.TRACKING_ERROR)
              callbacksRef.current.onTrackingError?.()
              return
            }

            consecutiveFailures += 1
            if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
              setStatus(EXPORT_JOB_STATUS.TRACKING_ERROR)
              callbacksRef.current.onTrackingError?.()
              return
            }
            scheduleNext()
          }
        }
      )
    }

    poll()

    return () => {
      isActive = false
      window.clearTimeout(timerId)
    }
  }, [getExportStatus, job, storageKey])

  return {
    status,
    isLongRunning,
    isExporting: Boolean(job) && ACTIVE_STATUSES.includes(status),
    startTracking
  }
}

export default useExportJob
